import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Search, SlidersHorizontal, X, ShoppingCart, Heart, Package, Truck, RotateCcw,
  ShieldCheck, ArrowRight, CheckCircle2, MapPin, Phone, Mail, MessageCircle,
  ChevronDown, Store, Globe, TrendingUp, Users, BadgeCheck, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate, imgUrl } from "@/lib/api";
import {
  DP, IMG, SiteShell, Reveal, SectionHead, GreenBtn, GhostBtn, ProductCard,
  Rating, PageHero, addToCart,
} from "./site";

function setMeta(name, content, og = false) {
  if (!content) return;
  const attr = og ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

// ============================= SHOP =============================
export function ShopPage() {
  const [sp, setSp] = useSearchParams();
  const [mobileFilters, setMobileFilters] = useState(false);
  const search = sp.get("search") || "";
  const category = sp.get("category") || "";
  const sort = sp.get("sort") || "newest";
  const page = Number(sp.get("page") || 1);
  const minPrice = sp.get("min_price") || "";
  const maxPrice = sp.get("max_price") || "";
  const minRating = sp.get("min_rating") || "";
  const setP = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };

  const { data: cats } = useQuery({ queryKey: ["dp-cats"], queryFn: async () => (await api.get("/public/catalog/categories")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["dp-shop", search, category, sort, page, minPrice, maxPrice, minRating],
    queryFn: async () => (await api.get(`/public/catalog/products?search=${encodeURIComponent(search)}&category=${encodeURIComponent(category)}&sort=${sort}&page=${page}&min_price=${minPrice || 0}&max_price=${maxPrice || 0}&min_rating=${minRating || 0}`)).data,
  });

  const Filters = (
    <div className="space-y-5" data-testid="shop-filters">
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-wider" style={{ color: DP.text }}>Categories</p>
        <button data-testid="filter-cat-all" onClick={() => setP("category", "")}
          className={`mb-1 block w-full rounded-lg px-3 py-1.5 text-left text-xs font-semibold ${!category ? "text-white" : ""}`}
          style={!category ? { background: DP.royal } : { color: DP.muted }}>All Categories</button>
        {(cats?.items || []).map((c) => (
          <button key={c.name} data-testid={`filter-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setP("category", c.name)}
            className={`mb-1 flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-xs font-semibold ${category === c.name ? "text-white" : ""}`}
            style={category === c.name ? { background: DP.royal } : { color: DP.muted }}>
            {c.name} <span className="text-[9px] opacity-70">{c.count}</span>
          </button>
        ))}
      </div>
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-wider" style={{ color: DP.text }}>Price (₹)</p>
        <div className="flex gap-2">
          <input data-testid="filter-min-price" type="number" placeholder="Min" value={minPrice} onChange={(e) => setP("min_price", e.target.value)}
            className="w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none" style={{ borderColor: DP.border }} />
          <input data-testid="filter-max-price" type="number" placeholder="Max" value={maxPrice} onChange={(e) => setP("max_price", e.target.value)}
            className="w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none" style={{ borderColor: DP.border }} />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-wider" style={{ color: DP.text }}>Rating</p>
        {[4, 3].map((r) => (
          <button key={r} data-testid={`filter-rating-${r}`} onClick={() => setP("min_rating", minRating === String(r) ? "" : String(r))}
            className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${minRating === String(r) ? "text-white" : ""}`}
            style={minRating === String(r) ? { background: DP.royal } : { color: DP.muted }}>
            <Rating value={r} /> & up
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <SiteShell>
      <PageHero title={category || "Shop All Products"} sub="Premium products with real margins — browse, then sell them as a DropPartner." crumbs={["Shop", ...(category ? [category] : [])]} />
      <div className="mx-auto max-w-7xl px-4 py-10">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-full border bg-white px-4 py-2" style={{ borderColor: DP.border, minWidth: 220 }}>
            <Search size={14} style={{ color: DP.muted }} />
            <input data-testid="shop-search" value={search} onChange={(e) => setP("search", e.target.value)}
              placeholder="Search products…" className="w-full bg-transparent text-sm outline-none" />
          </div>
          <select data-testid="shop-sort" value={sort} onChange={(e) => setP("sort", e.target.value)}
            className="rounded-full border bg-white px-4 py-2 text-xs font-bold outline-none" style={{ borderColor: DP.border, color: DP.text }}>
            <option value="newest">Newest</option><option value="price_low">Price: Low → High</option>
            <option value="price_high">Price: High → Low</option><option value="rating">Top Rated</option>
          </select>
          <button data-testid="mobile-filter-btn" onClick={() => setMobileFilters(true)}
            className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold lg:hidden" style={{ borderColor: DP.border, color: DP.royal }}>
            <SlidersHorizontal size={13} /> Filters
          </button>
        </div>
        <div className="grid gap-8 lg:grid-cols-[230px_1fr]">
          <aside className="hidden rounded-2xl border bg-white p-5 lg:block" style={{ borderColor: DP.border }}>{Filters}</aside>
          <div>
            <p className="mb-4 text-xs font-semibold" style={{ color: DP.muted }} data-testid="shop-count">{data ? `${data.total} products` : "Loading…"}</p>
            {isLoading ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-slate-100" />)}</div>
            ) : !data?.items?.length ? (
              <div className="rounded-2xl border border-dashed p-16 text-center" style={{ borderColor: DP.border }} data-testid="shop-empty">
                <Package size={34} className="mx-auto text-blue-200" />
                <p className="mt-3 text-sm font-bold" style={{ color: DP.text }}>No products found</p>
                <p className="mt-1 text-xs" style={{ color: DP.muted }}>Try a different search or clear filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
                {data.items.map((p) => <ProductCard key={p.id} p={p} />)}
              </div>
            )}
            {data && data.pages > 1 && (
              <div className="mt-8 flex justify-center gap-2" data-testid="shop-pager">
                {[...Array(data.pages)].map((_, i) => (
                  <button key={i} data-testid={`shop-page-${i + 1}`} onClick={() => setP("page", String(i + 1))}
                    className={`h-9 w-9 rounded-full text-xs font-extrabold ${page === i + 1 ? "text-white" : "border"}`}
                    style={page === i + 1 ? { background: DP.royal } : { borderColor: DP.border, color: DP.text }}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {mobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-extrabold">Filters</p>
              <button onClick={() => setMobileFilters(false)} data-testid="mobile-filter-close"><X size={18} /></button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </SiteShell>
  );
}

// ============================= CATEGORIES =============================
export function CategoriesPage() {
  const { data: cats } = useQuery({ queryKey: ["dp-cats"], queryFn: async () => (await api.get("/public/catalog/categories")).data });
  return (
    <SiteShell>
      <PageHero title="Browse Categories" sub="Every category is stocked with verified, high-margin products ready to sell." crumbs={["Categories"]} />
      <div className="mx-auto max-w-7xl px-4 py-12">
        {!cats?.items?.length ? (
          <div className="rounded-2xl border border-dashed p-16 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="categories-empty">Categories are being curated — explore all products meanwhile.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="categories-grid">
            {cats.items.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.04}>
                <Link to={`/shop?category=${encodeURIComponent(c.name)}`} data-testid={`cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group block overflow-hidden rounded-3xl border bg-white shadow-[0_6px_22px_rgba(11,25,44,0.06)] transition-all hover:-translate-y-1.5 hover:shadow-[0_22px_48px_rgba(11,25,44,0.14)]"
                  style={{ borderColor: DP.border }}>
                  <div className="flex h-36 items-center justify-center overflow-hidden" style={{ background: DP.ice }}>
                    {c.image ? <img src={imgUrl(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      : <Package size={36} className="text-blue-200" />}
                  </div>
                  <div className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-sm font-extrabold" style={{ color: DP.text }}>{c.name}</p>
                      <p className="text-[10px] font-semibold" style={{ color: DP.muted }}>{c.count} products</p>
                    </div>
                    <ArrowRight size={15} style={{ color: DP.green }} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </SiteShell>
  );
}

// ============================= DEALS =============================
export function DealsPage() {
  const { data } = useQuery({ queryKey: ["dp-deals"], queryFn: async () => (await api.get("/public/catalog/products?min_discount=15&sort=discount&limit=48")).data });
  return (
    <SiteShell>
      <PageHero title="Deals & Offers" sub="Biggest markdowns in the catalog — higher discounts mean stronger selling hooks." crumbs={["Deals"]} />
      <div className="mx-auto max-w-7xl px-4 py-10">
        <Reveal>
          <div className="relative mb-8 overflow-hidden rounded-3xl p-8 text-white sm:p-10" style={{ background: `linear-gradient(120deg, ${DP.navy}, ${DP.royal})` }} data-testid="deals-banner">
            <img src={IMG.mega} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
            <div className="relative">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-emerald-300">Mega Discounts</p>
              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>Up To 70% OFF</h2>
              <p className="mt-2 max-w-md text-sm text-blue-100">Limited-period pricing across trending products.</p>
            </div>
          </div>
        </Reveal>
        {!data?.items?.length ? (
          <div className="rounded-2xl border border-dashed p-16 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="deals-empty">No live deals right now — new offers drop every week.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="deals-grid">
            {data.items.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </SiteShell>
  );
}

// ============================= PRODUCT DETAIL =============================
export function ProductPage() {
  const { id } = useParams();
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const [tab, setTab] = useState("description");
  const { data, isLoading } = useQuery({
    queryKey: ["dp-product", id],
    queryFn: async () => (await api.get(`/public/catalog/products/${id}`)).data,
  });
  const p = data?.product;
  useEffect(() => {
    if (!p) return;
    document.title = p.meta_title || `${p.name} — DropPartner`;
    setMeta("description", p.meta_description || p.short_description || "");
    setMeta("keywords", p.keywords || "");
    setMeta("og:title", p.meta_title || p.name, true);
    setMeta("og:description", p.meta_description || p.short_description || "", true);
    setMeta("og:image", imgUrl((p.images || [])[0] || ""), true);
  }, [p]);
  return (
    <SiteShell>
      <div className="mx-auto max-w-7xl px-4 py-10" data-testid="product-page">
        {isLoading || !p ? (
          <div className="grid gap-10 lg:grid-cols-2"><div className="aspect-square animate-pulse rounded-3xl bg-slate-100" /><div className="space-y-4"><div className="h-8 w-2/3 animate-pulse rounded bg-slate-100" /><div className="h-6 w-1/3 animate-pulse rounded bg-slate-100" /><div className="h-24 animate-pulse rounded bg-slate-100" /></div></div>
        ) : (
          <>
            <p className="mb-5 text-[11px] font-bold" style={{ color: DP.muted }}><Link to="/" className="hover:text-[#1D4ED8]">Home</Link> / <Link to="/shop" className="hover:text-[#1D4ED8]">Shop</Link> / <span style={{ color: DP.text }}>{p.name}</span></p>
            <div className="grid gap-10 lg:grid-cols-2">
              <div>
                <div className="overflow-hidden rounded-3xl border" style={{ borderColor: DP.border }} data-testid="product-gallery">
                  {(p.images || [])[img]
                    ? <img src={imgUrl(p.images[img])} alt={p.name} className="aspect-square w-full object-cover" />
                    : <div className="flex aspect-square w-full items-center justify-center" style={{ background: DP.ice }}><Package size={60} className="text-blue-200" /></div>}
                </div>
                {(p.images || []).length > 1 && (
                  <div className="mt-3 flex gap-2">
                    {p.images.map((im, i) => (
                      <button key={i} data-testid={`thumb-${i}`} onClick={() => setImg(i)}
                        className={`h-16 w-16 overflow-hidden rounded-xl border-2 ${img === i ? "border-[#1D4ED8]" : "border-transparent"}`}>
                        <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em]" style={{ color: DP.bright }}>{p.category}</p>
                <h1 className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }} data-testid="product-title">{p.name}</h1>
                <div className="mt-2"><Rating value={p.rating || 4.3} count={p.reviews_count ?? 128} /></div>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-3xl font-extrabold" style={{ color: DP.text }} data-testid="product-price">{fmtINR(p.price)}</span>
                  {p.mrp > p.price && <span className="text-lg text-slate-400 line-through">{fmtINR(p.mrp)}</span>}
                  {p.discount_pct > 0 && <span className="rounded-full px-2.5 py-1 text-xs font-extrabold text-white" style={{ background: DP.green }}>{p.discount_pct}% OFF</span>}
                </div>
                <p className="mt-2 flex items-center gap-1.5 text-xs font-bold" style={{ color: DP.greenD }}><CheckCircle2 size={13} /> In stock — ships across India</p>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: DP.muted }}>{p.description || "Premium quality product from the DropPartner verified catalog."}</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex items-center rounded-full border" style={{ borderColor: DP.border }}>
                    <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2.5 font-extrabold">−</button>
                    <span className="w-8 text-center text-sm font-extrabold" data-testid="qty-value">{qty}</span>
                    <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-4 py-2.5 font-extrabold">+</button>
                  </div>
                  <button data-testid="add-to-cart-btn" onClick={() => { addToCart(p, qty); toast.success("Added to cart"); }}
                    className="flex-1 rounded-full py-3.5 text-sm font-extrabold text-white transition-transform hover:scale-[1.02]" style={{ background: DP.royal }}>
                    <ShoppingCart size={15} className="mr-2 inline" /> Add To Cart
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Link to="/register" data-testid="buy-now-btn" className="rounded-full py-3.5 text-center text-sm font-extrabold text-white transition-transform hover:scale-[1.02]" style={{ background: DP.green }}>Buy Now</Link>
                  <Link to="/become-a-partner" data-testid="add-to-store-btn" className="rounded-full border-2 py-3.5 text-center text-sm font-extrabold transition-colors hover:bg-blue-50" style={{ borderColor: DP.royal, color: DP.royal }}>
                    <Store size={14} className="mr-1.5 inline" /> Sell This Product
                  </Link>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-2 text-center">
                  {[[Truck, "Fast Shipping"], [RotateCcw, "Easy Returns"], [ShieldCheck, "Secure Payments"]].map(([Icon, t]) => (
                    <div key={t} className="rounded-xl border p-3" style={{ borderColor: DP.border }}>
                      <Icon size={16} className="mx-auto" style={{ color: DP.green }} />
                      <p className="mt-1 text-[10px] font-bold" style={{ color: DP.text }}>{t}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-12" data-testid="product-tabs">
              <div className="flex gap-1 overflow-x-auto border-b" style={{ borderColor: DP.border }}>
                {[["description", "Description"], ["specs", "Specifications"], ["shipping", "Shipping"], ["returns", "Returns"]].map(([k, l]) => (
                  <button key={k} data-testid={`ptab-${k}`} onClick={() => setTab(k)}
                    className={`shrink-0 px-5 py-3 text-xs font-extrabold ${tab === k ? "border-b-2" : ""}`}
                    style={{ color: tab === k ? DP.royal : DP.muted, borderColor: DP.royal }}>{l}</button>
                ))}
              </div>
              <div className="py-6 text-sm leading-relaxed" style={{ color: DP.muted }}>
                {tab === "description" && (p.description || "A premium, quality-checked product from the DropPartner catalog — ideal for marketplace and own-store selling.")}
                {tab === "specs" && (
                  (p.specs && Object.keys(p.specs).length)
                    ? <div className="grid max-w-lg gap-2">{Object.entries(p.specs).map(([k, v]) => (<div key={k} className="flex justify-between border-b py-2 text-xs" style={{ borderColor: DP.border }}><span className="font-bold" style={{ color: DP.text }}>{k}</span><span>{v}</span></div>))}</div>
                    : "Detailed specifications are provided by the brand and verified by our catalog team."
                )}
                {tab === "shipping" && "Dispatched within 24–48 hours by DropPartner logistics. Pan-India delivery in 3–7 business days with live tracking. COD available on eligible pincodes."}
                {tab === "returns" && "7-day easy returns on damaged, defective or wrong items. Refunds are processed to the original payment method within 5–7 business days after pickup."}
              </div>
            </div>
            {(data?.related || []).length > 0 && (
              <div className="mt-12">
                <SectionHead title="Related Products" center={false} />
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="related-products">
                  {data.related.slice(0, 4).map((r) => <ProductCard key={r.id} p={r} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </SiteShell>
  );
}

// ============================= CART =============================
export function CartPage() {
  const [, force] = useState(0);
  const cart = JSON.parse(localStorage.getItem("dp_cart") || "[]");
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const update = (id, qty) => {
    const c = cart.map((i) => (i.id === id ? { ...i, qty } : i)).filter((i) => i.qty > 0);
    localStorage.setItem("dp_cart", JSON.stringify(c));
    window.dispatchEvent(new Event("dp-cart"));
    force((x) => x + 1);
  };
  return (
    <SiteShell>
      <PageHero title="Your Cart" sub="Review your products before checkout." crumbs={["Cart"]} />
      <div className="mx-auto max-w-4xl px-4 py-10" data-testid="cart-page">
        {!cart.length ? (
          <div className="rounded-2xl border border-dashed p-16 text-center" style={{ borderColor: DP.border }} data-testid="cart-empty">
            <ShoppingCart size={36} className="mx-auto text-blue-200" />
            <p className="mt-3 text-sm font-bold" style={{ color: DP.text }}>Your cart is empty</p>
            <div className="mt-4"><GreenBtn to="/shop" testid="cart-shop-btn">Continue Shopping</GreenBtn></div>
          </div>
        ) : (
          <>
            {cart.map((i) => (
              <div key={i.id} className="mb-3 flex items-center gap-4 rounded-2xl border bg-white p-4" style={{ borderColor: DP.border }} data-testid={`cart-item-${i.id}`}>
                {i.image ? <img src={imgUrl(i.image)} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-xl" style={{ background: DP.ice }}><Package size={20} className="text-blue-200" /></div>}
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: DP.text }}>{i.name}</p>
                  <p className="text-xs" style={{ color: DP.muted }}>{fmtINR(i.price)}</p>
                </div>
                <div className="flex items-center rounded-full border" style={{ borderColor: DP.border }}>
                  <button data-testid={`cart-minus-${i.id}`} onClick={() => update(i.id, i.qty - 1)} className="px-3 py-1.5 font-extrabold">−</button>
                  <span className="w-7 text-center text-xs font-extrabold">{i.qty}</span>
                  <button data-testid={`cart-plus-${i.id}`} onClick={() => update(i.id, i.qty + 1)} className="px-3 py-1.5 font-extrabold">+</button>
                </div>
                <p className="w-20 text-right text-sm font-extrabold" style={{ color: DP.text }}>{fmtINR(i.price * i.qty)}</p>
              </div>
            ))}
            <div className="mt-6 flex items-center justify-between rounded-2xl p-5 text-white" style={{ background: DP.navy }} data-testid="cart-summary">
              <div>
                <p className="text-xs text-blue-200">Total</p>
                <p className="text-2xl font-extrabold">{fmtINR(total)}</p>
              </div>
              <GreenBtn to="/register" testid="checkout-btn">Proceed To Checkout</GreenBtn>
            </div>
            <p className="mt-3 text-center text-[11px]" style={{ color: DP.muted }}>Checkout happens inside your free DropPartner account — create one in under a minute.</p>
          </>
        )}
      </div>
    </SiteShell>
  );
}

// ============================= HOW IT WORKS =============================
const JOURNEY = [
  ["Join", "Create your free DropPartner account and pick a partner plan.", DP.royal],
  ["Choose", "Browse the verified catalog — real stock, real margins, trending products.", DP.bright],
  ["List", "Add products to your own store or send them for Amazon/Flipkart publishing.", DP.green],
  ["Customer Orders", "Your customers order on your store or marketplace listing.", "#0891B2"],
  ["Processing", "DropPartner reserves stock, packs and prepares the shipment.", "#7C3AED"],
  ["Shipping", "IndiaFast logistics delivers pan-India with live tracking.", DP.bright],
  ["Customer Receives", "Delivered. Returns and exchanges are handled for you.", DP.green],
  ["You Grow", "Profit settles to your wallet on delivery. Reinvest and scale.", "#059669"],
];

export function HowItWorksPage() {
  return (
    <SiteShell>
      <PageHero title="How DropPartner Works" sub="The complete journey — from joining to getting paid — with zero inventory on your side." crumbs={["How It Works"]} />
      <div className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal><img src={IMG.journey} alt="Dropshipping journey" className="w-full rounded-[2rem] border object-cover shadow-[0_30px_70px_rgba(11,25,44,0.16)]" style={{ borderColor: DP.border }} data-testid="journey-img" /></Reveal>
          <div className="space-y-3">
            {JOURNEY.slice(0, 4).map(([t, d, c], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="flex gap-4 rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: DP.border }} data-testid={`journey-${t.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold text-white" style={{ background: c }}>{i + 1}</span>
                  <div><p className="text-sm font-extrabold" style={{ color: DP.text }}>{t}</p><p className="mt-0.5 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {JOURNEY.slice(4).map(([t, d, c], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: DP.border }} data-testid={`journey-${t.toLowerCase().replace(/ /g, "-")}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl text-sm font-extrabold text-white" style={{ background: c }}>{i + 5}</span>
                <p className="mt-3 text-sm font-extrabold" style={{ color: DP.text }}>{t}</p>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center"><GreenBtn to="/become-a-partner" testid="hiw-cta">Become a Partner Today</GreenBtn></div>
      </div>
    </SiteShell>
  );
}

// ============================= PARTNER PLANS =============================
export function PartnerPlansPage() {
  const { data: plans } = useQuery({ queryKey: ["dp-plans"], queryFn: async () => (await api.get("/plans")).data });
  const items = plans || [];
  const mid = items.length === 3 ? 1 : Math.floor(items.length / 2);
  return (
    <SiteShell>
      <PageHero title="Partner Plans" sub="One-time setup, year-one service free. Pick the catalog size that fits your ambition." crumbs={["For Partners", "Plans"]} />
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-6 md:grid-cols-3" data-testid="plans-grid">
          {items.map((p, i) => {
            const pop = i === mid;
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                <div data-testid={`plan-card-${p.slug}`}
                  className={`relative flex h-full flex-col rounded-3xl border-2 bg-white p-7 transition-all duration-300 hover:-translate-y-2 ${pop ? "shadow-[0_28px_60px_rgba(16,185,129,0.22)]" : "shadow-[0_8px_28px_rgba(11,25,44,0.07)] hover:shadow-[0_22px_48px_rgba(11,25,44,0.14)]"}`}
                  style={{ borderColor: pop ? DP.green : DP.border }}>
                  {pop && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-lg" style={{ background: DP.green }}>Most Popular</span>}
                  <p className="text-sm font-extrabold uppercase tracking-wider" style={{ color: DP.royal }}>{p.name}</p>
                  <p className="mt-3 text-4xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }}>{fmtINR(p.price)}</p>
                  <p className="text-[11px] font-semibold" style={{ color: DP.muted }}>one-time · year-1 service free</p>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {(p.features || [`${p.max_products} products`, "Own online store", "Marketplace selling", "Shipping handled", "Profit dashboard"]).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs font-semibold" style={{ color: DP.text }}>
                        <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: DP.green }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" data-testid={`plan-cta-${p.slug}`}
                    className={`mt-6 rounded-full py-3 text-center text-sm font-extrabold transition-transform hover:scale-[1.03] ${pop ? "text-white" : "border-2"}`}
                    style={pop ? { background: DP.green } : { borderColor: DP.royal, color: DP.royal }}>
                    Get Started <ArrowRight size={13} className="ml-1 inline" />
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>
        {!items.length && <div className="rounded-2xl border border-dashed p-16 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="plans-empty">Plans are loading — please check back shortly.</div>}
        <p className="mt-8 text-center text-xs" style={{ color: DP.muted }}>Marketplace item plans (25/50/75/100 products) are available separately inside your partner dashboard — first term is 3 months prepaid, then billed monthly.</p>
      </div>
    </SiteShell>
  );
}

// ============================= BECOME A PARTNER =============================
export function BecomePartnerPage() {
  return (
    <SiteShell>
      <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: `linear-gradient(135deg, ${DP.navy}, ${DP.royal})` }} data-testid="bp-hero">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full opacity-25 blur-3xl" style={{ background: DP.green }} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-emerald-300">Partner Program</p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-5xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              Build Your Online Business With <span style={{ color: DP.green }}>DropPartner</span>
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-blue-100 sm:text-base">
              Zero inventory. Premium products. Marketplaces plus your own branded store. We run operations — you run the business.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <GreenBtn to="/register" testid="bp-join">Become a Partner</GreenBtn>
              <Link to="/partner-plans" data-testid="bp-plans" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">See Partner Plans</Link>
            </div>
          </motion.div>
          <motion.img initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            src={IMG.marketplaces} alt="Sell everywhere" className="mx-auto w-full max-w-md rounded-[2rem] border-4 border-white/10 object-cover shadow-2xl" style={{ animation: "dpfloat 5s ease-in-out infinite" }} />
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 py-16">
        <SectionHead eyebrow="Why partners win" title="Everything Included" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="bp-benefits">
          {[
            [Package, "Zero Inventory", "Never buy stock in bulk — products are reserved only when your customer orders."],
            [BadgeCheck, "Premium Products", "A quality-checked catalog with real margins, images and descriptions."],
            [Globe, "Marketplace Selling", "Amazon and Flipkart publishing handled with admin approval flow."],
            [Store, "Your Own Store", "7 premium storefront templates, your branding and your domain."],
            [Truck, "Operational Support", "Packing, shipping, tracking, returns — handled end to end."],
            [TrendingUp, "Business Growth", "Wallet, profit analytics, AI marketing tools and plan upgrades as you scale."],
          ].map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="h-full rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-[0_18px_44px_rgba(11,25,44,0.12)]" style={{ borderColor: DP.border }} data-testid={`benefit-${t.toLowerCase().replace(/ /g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-md" style={{ background: `linear-gradient(135deg, ${DP.royal}, ${DP.green})` }}><Icon size={19} /></span>
                <p className="mt-4 text-sm font-extrabold" style={{ color: DP.text }}>✓ {t}</p>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center"><GreenBtn to="/register" testid="bp-join-bottom">Start Free — Join Now</GreenBtn></div>
      </div>
    </SiteShell>
  );
}

// ============================= TRACK ORDER =============================
const TRACK_STEPS = ["placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"];

export function TrackOrderPage() {
  const [form, setForm] = useState({ order_no: "", mobile: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/public/track", form);
      setResult(data.order);
    } catch (err) { toast.error(err?.response?.data?.detail || "Order not found"); } finally { setBusy(false); }
  };
  const stepIdx = result ? Math.max(0, TRACK_STEPS.indexOf((result.status || "").toLowerCase())) : -1;
  return (
    <SiteShell>
      <PageHero title="Track Your Order" sub="Enter your order number and the mobile number used at checkout." crumbs={["Track Order"]} />
      <div className="mx-auto max-w-2xl px-4 py-14" data-testid="track-page">
        <form onSubmit={search} className="rounded-3xl border bg-white p-7 shadow-[0_10px_36px_rgba(11,25,44,0.08)]" style={{ borderColor: DP.border }}>
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-extrabold" style={{ color: DP.text }}>Order number</p>
              <input data-testid="track-order-no" value={form.order_no} onChange={(e) => setForm({ ...form, order_no: e.target.value })}
                placeholder="e.g. IF-20260907-XXXX" className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1D4ED8]" style={{ borderColor: DP.border }} />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-extrabold" style={{ color: DP.text }}>Mobile number</p>
              <input data-testid="track-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="10-digit mobile" className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1D4ED8]" style={{ borderColor: DP.border }} />
            </div>
            <button data-testid="track-submit" disabled={busy}
              className="w-full rounded-full py-3.5 text-sm font-extrabold text-white transition-transform hover:scale-[1.02]" style={{ background: DP.green }}>
              {busy ? "Tracking…" : "Track Order"}
            </button>
          </div>
        </form>
        {result && (
          <Reveal className="mt-8">
            <div className="rounded-3xl border bg-white p-7 shadow-sm" style={{ borderColor: DP.border }} data-testid="track-result">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-sm font-extrabold" style={{ color: DP.text }}>{result.order_no}</p>
                <span className="rounded-full px-3 py-1 text-[11px] font-extrabold uppercase text-white" style={{ background: DP.royal }} data-testid="track-status">{(result.status || "").replace(/_/g, " ")}</span>
              </div>
              <p className="mt-1 text-xs" style={{ color: DP.muted }}>{fmtDate(result.created_at)} · {result.city}{result.state ? `, ${result.state}` : ""} · {fmtINR(result.total)} · {(result.payment_method || "").toUpperCase()}</p>
              {result.awb && <p className="mt-1 text-xs font-semibold" style={{ color: DP.bright }}>AWB: {result.awb}</p>}
              <div className="mt-6 flex items-center">
                {TRACK_STEPS.map((s, i) => (
                  <div key={s} className="flex flex-1 items-center last:flex-none">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold ${i <= stepIdx ? "text-white" : "border-2"}`}
                      style={i <= stepIdx ? { background: DP.green } : { borderColor: DP.border, color: DP.muted }} data-testid={`track-step-${s}`}>
                      {i <= stepIdx ? "✓" : i + 1}
                    </div>
                    {i < TRACK_STEPS.length - 1 && <div className="h-0.5 flex-1" style={{ background: i < stepIdx ? DP.green : DP.border }} />}
                  </div>
                ))}
              </div>
              <div className="mt-1.5 flex justify-between text-[8px] font-bold uppercase" style={{ color: DP.muted }}>
                <span>Placed</span><span>Confirmed</span><span>Packed</span><span>Shipped</span><span>Out</span><span>Delivered</span>
              </div>
              <div className="mt-5 space-y-1 border-t pt-4" style={{ borderColor: DP.border }}>
                {result.items.map((i, idx) => (
                  <p key={idx} className="text-xs" style={{ color: DP.muted }}>{i.name} × {i.qty} — {fmtINR(i.price * i.qty)}</p>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </SiteShell>
  );
}

// ============================= ABOUT =============================
export function AboutPage() {
  return (
    <SiteShell>
      <PageHero title="About DropPartner" sub="We're building India's most trusted dropshipping platform for everyday entrepreneurs." crumbs={["About Us"]} />
      <div className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-2xl font-extrabold sm:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }}>Who We Are</h2>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: DP.muted }}>
              DropPartner started with a simple observation: millions of Indians want to sell online, but inventory, logistics and technology
              stand in the way. We remove all three. Our platform combines a verified product catalog, marketplace publishing,
              branded online stores and pan-India shipping into one dashboard — so anyone can start selling with zero inventory.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[["Our Mission", "Make online selling accessible to every Indian entrepreneur — no capital, no warehouse, no code."],
                ["Our Vision", "A million profitable partner businesses powered by DropPartner by 2030."],
                ["Why DropPartner", "One platform for products, stores, marketplaces and shipping — with honest pricing and real support."],
                ["Our Values", "Partner-first, transparency, reliability and long-term trust over short-term gain."]].map(([t, d]) => (
                <div key={t} className="rounded-2xl border bg-white p-5" style={{ borderColor: DP.border }} data-testid={`about-${t.toLowerCase().replace(/ /g, "-")}`}>
                  <p className="text-sm font-extrabold" style={{ color: DP.royal }}>{t}</p>
                  <p className="mt-1.5 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}><img src={IMG.office} alt="DropPartner team" className="w-full rounded-[2rem] border object-cover shadow-[0_30px_70px_rgba(11,25,44,0.16)]" style={{ borderColor: DP.border }} data-testid="about-img" /></Reveal>
        </div>
        <div className="mt-16 grid gap-5 sm:grid-cols-3" data-testid="journey-strip">
          {[["2024", "Founded in Hyderabad — first 100 partners onboarded."], ["2025", "Marketplace publishing + own-store builder launched."], ["2026", "10,000+ partners, 1M+ products, AI marketing tools live."]].map(([y, d], i) => (
            <Reveal key={y} delay={i * 0.08}>
              <div className="rounded-2xl border bg-white p-6 text-center" style={{ borderColor: DP.border }}>
                <p className="text-2xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif", color: DP.green }}>{y}</p>
                <p className="mt-2 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center"><GreenBtn to="/become-a-partner" testid="about-cta">Join The Journey</GreenBtn></div>
      </div>
    </SiteShell>
  );
}

// ============================= CONTACT =============================
export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      toast.success("Message sent — we'll reply within 24 hours");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <SiteShell>
      <PageHero title="Contact Us" sub="Questions about partnering, products or orders — we reply within one business day." crumbs={["Contact Us"]} />
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-8 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2" data-testid="contact-info">
            {[
              [Phone, "Call Us", "+91 90000 00000", "Mon–Sat, 10am–7pm IST"],
              [Mail, "Email", "support@droppartner.in", "Replies within 24 hours"],
              [MessageCircle, "WhatsApp", "+91 90000 00000", "Fastest response"],
              [MapPin, "Office", "Hyderabad, Telangana, India", "Visit by appointment"],
            ].map(([Icon, t, v, s]) => (
              <div key={t} className="flex gap-4 rounded-2xl border bg-white p-5" style={{ borderColor: DP.border }} data-testid={`contact-${t.toLowerCase().replace(/ /g, "-")}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: DP.royal }}><Icon size={18} /></span>
                <div>
                  <p className="text-sm font-extrabold" style={{ color: DP.text }}>{t}</p>
                  <p className="text-xs font-bold" style={{ color: DP.bright }}>{v}</p>
                  <p className="text-[10px]" style={{ color: DP.muted }}>{s}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-3">
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center rounded-3xl border bg-white p-14 text-center" style={{ borderColor: DP.border }} data-testid="contact-success">
                <CheckCircle2 size={44} style={{ color: DP.green }} />
                <h3 className="mt-4 text-xl font-extrabold" style={{ color: DP.text }}>Message received!</h3>
                <p className="mt-2 text-sm" style={{ color: DP.muted }}>Our team will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border bg-white p-7 shadow-[0_10px_36px_rgba(11,25,44,0.08)]" style={{ borderColor: DP.border }} data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  {[["name", "Your name", "text"], ["email", "Email", "email"], ["phone", "Phone (optional)", "tel"]].map(([k, l, type]) => (
                    <div key={k}>
                      <p className="mb-1.5 text-xs font-extrabold" style={{ color: DP.text }}>{l}</p>
                      <input data-testid={`contact-${k}`} type={type} placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1D4ED8]" style={{ borderColor: DP.border }} />
                    </div>
                  ))}
                  <div>
                    <p className="mb-1.5 text-xs font-extrabold" style={{ color: DP.text }}>Subject</p>
                    <select data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full rounded-xl border px-4 py-3 text-sm outline-none" style={{ borderColor: DP.border }}>
                      {["General", "Partnership", "Products", "Orders", "Payments", "Technical"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="mb-1.5 text-xs font-extrabold" style={{ color: DP.text }}>Message</p>
                  <textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1D4ED8]" style={{ borderColor: DP.border }} />
                </div>
                <button data-testid="contact-submit" disabled={busy}
                  className="mt-5 w-full rounded-full py-3.5 text-sm font-extrabold text-white transition-transform hover:scale-[1.02]" style={{ background: DP.green }}>
                  {busy ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </SiteShell>
  );
}

// ============================= FAQ =============================
const FAQ_DATA = {
  General: [
    ["What is DropPartner?", "DropPartner is India's dropshipping platform — you choose products from our catalog and sell them on marketplaces or your own store while we handle inventory, packing and shipping."],
    ["Do I need investment to start?", "No inventory investment. You only pick a partner plan for platform access — products are reserved centrally when your customer orders."],
  ],
  Partners: [
    ["How do I become a partner?", "Click Join Now, complete registration, choose a plan and your dashboard activates instantly with catalog, store builder and marketplace tools."],
    ["Can I upgrade or downgrade my plan?", "Yes — upgrades apply immediately; downgrades apply once your product usage fits the smaller plan."],
  ],
  Products: [
    ["Are products quality-checked?", "Yes — every product in the catalog is verified for quality, images and accurate descriptions before listing."],
    ["Can I suggest products to add?", "Absolutely — partners can request products via the support desk and our sourcing team reviews them."],
  ],
  Orders: [
    ["Who handles shipping?", "IndiaFast logistics — automatic AWB, labels, pickup and live tracking for every order."],
    ["What about returns and RTO?", "Returns, NDR and RTO are managed from your dashboard with full visibility."],
  ],
  Payments: [
    ["When do I get my profit?", "Profit settles to your DropPartner wallet on order delivery. You can withdraw to your bank anytime."],
    ["Are payments secure?", "Yes — all payments are processed through PCI-DSS compliant gateways."],
  ],
  Shipping: [
    ["How fast is delivery?", "Pan-India delivery in 3–7 business days with live tracking on every shipment."],
    ["Is COD available?", "Yes — cash on delivery is available on eligible pincodes."],
  ],
  Returns: [
    ["What is the return policy?", "7-day easy returns for damaged, defective or wrong items. Refunds process within 5–7 business days after pickup."],
  ],
  Marketplace: [
    ["Can I sell on Amazon and Flipkart?", "Yes — select products in the Marketplace module, admin reviews and publishes them to your connected seller accounts."],
    ["Is there a marketplace limit?", "Marketplace plans control how many unique products you can allocate — 25/50/75/100, with a minimum 3-month prepaid first term, then monthly billing."],
  ],
  Store: [
    ["Can I use my own domain?", "Yes — connect your domain in My Store → Domain with guided DNS verification."],
    ["How many store designs are there?", "7 premium templates with drag-and-drop sections, AI images and full page/blog management."],
  ],
};

export function FAQPage() {
  const [cat, setCat] = useState("General");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(0);
  const cats = Object.keys(FAQ_DATA);
  const items = q.trim()
    ? Object.entries(FAQ_DATA).flatMap(([c, arr]) => arr.filter(([question]) => question.toLowerCase().includes(q.toLowerCase())).map((x) => [c, ...x]))
    : FAQ_DATA[cat].map((x) => [cat, ...x]);
  return (
    <SiteShell>
      <PageHero title="Frequently Asked Questions" sub="Search or browse by topic — can't find an answer? Contact us anytime." crumbs={["FAQ"]} />
      <div className="mx-auto max-w-4xl px-4 py-14" data-testid="faq-page">
        <div className="mb-6 flex items-center gap-2 rounded-full border bg-white px-4 py-2.5" style={{ borderColor: DP.border }}>
          <Search size={15} style={{ color: DP.muted }} />
          <input data-testid="faq-search" value={q} onChange={(e) => { setQ(e.target.value); setOpen(0); }} placeholder="Search questions…" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <div className="mb-6 flex flex-wrap gap-2" data-testid="faq-cats">
          {cats.map((c) => (
            <button key={c} data-testid={`faq-cat-${c.toLowerCase()}`} onClick={() => { setCat(c); setQ(""); setOpen(0); }}
              className={`rounded-full px-4 py-2 text-xs font-extrabold ${cat === c && !q ? "text-white" : "border"}`}
              style={cat === c && !q ? { background: DP.royal } : { borderColor: DP.border, color: DP.text }}>{c}</button>
          ))}
        </div>
        <div className="space-y-2.5">
          {items.map(([c, question, answer], i) => (
            <Reveal key={question} delay={i * 0.03}>
              <div className="overflow-hidden rounded-2xl border bg-white" style={{ borderColor: DP.border }}>
                <button data-testid={`faq-q-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-bold" style={{ color: DP.text }}>
                  <span>{q.trim() && <span className="mr-2 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase text-white" style={{ background: DP.green }}>{c}</span>}{question}</span>
                  <ChevronDown size={16} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: DP.royal }} />
                </button>
                {open === i && <p className="border-t px-5 py-4 text-sm leading-relaxed" style={{ borderColor: DP.border, color: DP.muted }} data-testid={`faq-a-${i}`}>{answer}</p>}
              </div>
            </Reveal>
          ))}
          {!items.length && <p className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="faq-empty">No matching questions — try different keywords.</p>}
        </div>
      </div>
    </SiteShell>
  );
}

// ============================= HELP CENTER =============================
export function HelpCenterPage() {
  return (
    <SiteShell>
      <PageHero title="Help Center" sub="Everything you need to sell with confidence — guides, tracking and direct support." crumbs={["Help Center"]} />
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="help-grid">
          {[
            [Truck, "Track Order", "Live status of any order with your order number and mobile.", "/track-order"],
            [Package, "Products & Catalog", "How products are sourced, verified and priced.", "/faq"],
            [Store, "Your Online Store", "Templates, domains, pages and store builder guides.", "/faq"],
            [Globe, "Marketplace Selling", "Amazon & Flipkart publishing, limits and sync.", "/faq"],
            [Users, "Partner Plans", "Plans, trials, billing and upgrades explained.", "/partner-plans"],
            [MessageCircle, "Contact Support", "Talk to a human — we reply within 24 hours.", "/contact-us"],
          ].map(([Icon, t, d, to], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <Link to={to} data-testid={`help-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group block h-full rounded-2xl border bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(11,25,44,0.12)]" style={{ borderColor: DP.border }}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${DP.royal}, ${DP.green})` }}><Icon size={19} /></span>
                <p className="mt-4 text-sm font-extrabold" style={{ color: DP.text }}>{t}</p>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-extrabold" style={{ color: DP.royal }}>Open <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </SiteShell>
  );
}

// ============================= BLOG =============================
export function BlogPage() {
  const { data } = useQuery({ queryKey: ["dp-blog"], queryFn: async () => (await api.get("/public/blog")).data });
  return (
    <SiteShell>
      <PageHero title="DropPartner Blog" sub="Playbooks, product trends and growth tactics for Indian online sellers." crumbs={["Blog"]} />
      <div className="mx-auto max-w-7xl px-4 py-14">
        {!data?.items?.length ? (
          <div className="rounded-2xl border border-dashed p-16 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="blog-empty">Fresh articles are on the way.</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {data.items.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.06}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-${b.slug}`}
                  className="group block overflow-hidden rounded-3xl border bg-white shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-[0_22px_48px_rgba(11,25,44,0.14)]" style={{ borderColor: DP.border }}>
                  <div className="overflow-hidden"><img src={b.cover} alt={b.title} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="p-6">
                    <span className="rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-white" style={{ background: DP.green }}>{b.category}</span>
                    <h3 className="mt-3 text-base font-extrabold leading-snug" style={{ color: DP.text }}>{b.title}</h3>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed" style={{ color: DP.muted }}>{b.excerpt}</p>
                    <p className="mt-3 text-[10px] font-bold" style={{ color: DP.muted }}>{fmtDate(b.published_at)} · {b.read_time} read</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </SiteShell>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const { data: post } = useQuery({ queryKey: ["dp-post", slug], queryFn: async () => (await api.get(`/public/blog/${slug}`)).data });
  if (!post) return <SiteShell><div className="p-20 text-center text-sm" style={{ color: DP.muted }}>Loading article…</div></SiteShell>;
  return (
    <SiteShell>
      <article className="mx-auto max-w-3xl px-4 py-14" data-testid="blog-post">
        <p className="text-[11px] font-bold" style={{ color: DP.muted }}><Link to="/blog" className="hover:text-[#1D4ED8]">Blog</Link> / {post.category}</p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }}>{post.title}</h1>
        <p className="mt-2 text-xs font-semibold" style={{ color: DP.muted }}>{post.author} · {fmtDate(post.published_at)} · {post.read_time} read</p>
        <img src={post.cover} alt={post.title} className="mt-6 w-full rounded-3xl border object-cover" style={{ borderColor: DP.border }} />
        <div className="mt-8 space-y-4 text-[15px] leading-relaxed" style={{ color: DP.muted }}>
          {(post.content || "").split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
        </div>
        <div className="mt-10 rounded-3xl p-8 text-center text-white" style={{ background: `linear-gradient(120deg, ${DP.navy}, ${DP.royal})` }}>
          <p className="text-lg font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>Ready to put this into practice?</p>
          <div className="mt-4"><GreenBtn to="/register" testid="post-cta">Start Selling Free</GreenBtn></div>
        </div>
      </article>
    </SiteShell>
  );
}

// ============================= LEGAL =============================
const LEGAL = {
  "terms-and-conditions": {
    title: "Terms & Conditions",
    sections: [
      ["Acceptance of Terms", "By accessing DropPartner you agree to these terms. DropPartner provides a dropshipping platform connecting partners with product catalogs, storefronts, marketplace publishing and logistics."],
      ["Partner Accounts", "Partners must provide accurate registration details including business name, mobile, email and KYC information where required. One account per business entity."],
      ["Plans & Billing", "Partner plans are one-time with annual service renewal from year two. Marketplace item plans require a minimum 3-month prepaid first term, then are billed monthly. Failed payments follow a grace and suspension workflow; data is never deleted for non-payment alone."],
      ["Product Selling", "Partners set selling prices within platform guidelines. Product images, descriptions and stock are provided by DropPartner and must not be misrepresented."],
      ["Orders & Fulfilment", "All fulfilment is handled by DropPartner logistics. Partners must not independently ship marketplace or store orders."],
      ["Prohibited Use", "Fraudulent orders, fake reviews, marketplace policy violations or misuse of AI-generated content will result in account suspension."],
      ["Liability", "DropPartner's liability is limited to the value of services purchased. We are not liable for marketplace account actions taken by Amazon or Flipkart."],
      ["Changes", "We may update these terms with notice. Continued use constitutes acceptance."],
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    sections: [
      ["Data We Collect", "Account details (name, mobile, email, business information), order and customer data for fulfilment, payment status (never full card details), and usage analytics."],
      ["How We Use Data", "To operate the platform: fulfilment, marketplace publishing, payouts, support, fraud prevention and product improvement."],
      ["Sharing", "Data is shared only with logistics partners, marketplaces you sell on, and payment processors — strictly as needed to deliver the service."],
      ["Your Customers' Data", "Customer data from your store is used solely for order fulfilment and never for DropPartner marketing."],
      ["Security", "Encrypted credentials, role-based access and audit logs protect your data. Marketplace tokens are stored encrypted."],
      ["Your Rights", "Request access, correction or deletion of your personal data by writing to support@droppartner.in."],
    ],
  },
  "return-policy": {
    title: "Return Policy",
    sections: [
      ["7-Day Returns", "Customers can return damaged, defective or incorrect items within 7 days of delivery."],
      ["Return Process", "Raise a return from the order page or via support. Pickup is arranged by DropPartner logistics; quality check happens at the warehouse."],
      ["Refunds", "Refunds are processed to the original payment method within 5–7 business days after pickup and QC."],
      ["Non-Returnable", "Innerwear, personal care and perishables are non-returnable unless damaged or defective."],
      ["Partner Impact", "Returned orders reverse the profit settlement in the partner wallet with full ledger visibility."],
    ],
  },
  "shipping-policy": {
    title: "Shipping Policy",
    sections: [
      ["Coverage", "Pan-India delivery across 27,000+ pincodes via IndiaFast logistics partners."],
      ["Timelines", "Dispatch within 24–48 hours of order; delivery in 3–7 business days depending on destination."],
      ["Tracking", "Every shipment carries a live AWB with tracking visible to partner and customer."],
      ["COD", "Cash on delivery is available on eligible pincodes; COD remittance follows the standard settlement cycle."],
      ["Failed Deliveries", "NDR cases get re-attempts; RTO shipments return to origin with status visibility."],
    ],
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    sections: [
      ["Order Cancellation", "Orders can be cancelled before dispatch at no charge from the dashboard or via support."],
      ["After Dispatch", "Post-dispatch cancellations are treated as returns per the Return Policy."],
      ["Subscription Cancellation", "Marketplace subscriptions can be cancelled anytime; service continues until the current cycle ends. Trial cancellations incur no charge."],
      ["Plan Refunds", "One-time partner plan fees are refundable within 7 days if no products have been published or orders processed."],
    ],
  },
};

export function LegalPage({ kind }) {
  const doc = LEGAL[kind];
  const [active, setActive] = useState(0);
  return (
    <SiteShell>
      <PageHero title={doc.title} sub="Last updated: September 2026 · DropPartner, Hyderabad, India" crumbs={[doc.title]} />
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block" data-testid="legal-toc">
            <div className="sticky top-32 space-y-1 rounded-2xl border bg-white p-4" style={{ borderColor: DP.border }}>
              <p className="mb-2 text-[10px] font-extrabold uppercase tracking-wider" style={{ color: DP.muted }}>On this page</p>
              {doc.sections.map(([t], i) => (
                <button key={t} data-testid={`legal-toc-${i}`} onClick={() => { setActive(i); document.getElementById(`legal-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-xs font-bold ${active === i ? "text-white" : ""}`}
                  style={active === i ? { background: DP.royal } : { color: DP.muted }}>{t}</button>
              ))}
            </div>
          </aside>
          <div className="space-y-8" data-testid="legal-content">
            {doc.sections.map(([t, d], i) => (
              <Reveal key={t}>
                <section id={`legal-${i}`} data-testid={`legal-section-${i}`}>
                  <h2 className="text-xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }}>
                    <span className="mr-2 text-sm font-extrabold" style={{ color: DP.green }}>{String(i + 1).padStart(2, "0")}</span>{t}
                  </h2>
                  <p className="mt-2.5 text-sm leading-relaxed" style={{ color: DP.muted }}>{d}</p>
                </section>
              </Reveal>
            ))}
            <div className="rounded-2xl border p-6 text-sm" style={{ borderColor: DP.border, background: DP.ice, color: DP.muted }}>
              Questions about {doc.title.toLowerCase()}? <Link to="/contact-us" className="font-extrabold" style={{ color: DP.royal }}>Contact us</Link> — we reply within 24 hours.
            </div>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}
